/**
 * Chat Notifications Hook
 * Handles browser notifications for incoming chat messages
 */

import { useEffect, useRef, useState } from 'react';
import { useChatWebSocket } from '../contexts/ChatWebSocketContext';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

type NotificationPermission = 'default' | 'granted' | 'denied';

export function useChatNotifications(currentChannelId?: string | null) {
  const { on } = useChatWebSocket();
  const { user } = useAuth();
  const location = useLocation();
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>('default');
  const [isWindowFocused, setIsWindowFocused] = useState(true);
  const lastNotificationRef = useRef<{ channelId: string; timestamp: number } | null>(null);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      
      if (Notification.permission === 'default') {
        // Request permission when user first interacts with chat
        // Don't auto-request, wait for user interaction
      }
    }
  }, []);

  // Track window focus
  useEffect(() => {
    const handleFocus = () => setIsWindowFocused(true);
    const handleBlur = () => setIsWindowFocused(false);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    try {
      // Use Web Audio API to play a simple beep
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // 800 Hz beep
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (e) {
      // Fallback: silent if Web Audio API fails
      console.warn('Could not play notification sound:', e);
    }
  };

  // Check if channel is muted (user opted out of notifications)
  const isChannelMuted = (chId: string): boolean => {
    try {
      const raw = localStorage.getItem('chat-muted-channels');
      if (!raw) return false;
      const arr = JSON.parse(raw);
      return Array.isArray(arr) && arr.includes(chId);
    } catch {
      return false;
    }
  };

  // Show browser notification
  const showNotification = (title: string, body: string, channelId: string) => {
    // Don't show notification if:
    // 1. Notifications are denied
    // 2. Channel is muted
    // 3. Message is from the channel user is currently viewing AND window is focused
    // 4. Same message was just notified (prevent duplicates)
    
    if (Notification.permission !== 'granted') {
      return;
    }

    if (isChannelMuted(channelId)) {
      return;
    }

    // Don't notify if user is viewing this channel and window is focused
    const isOnChatPage = location.pathname === '/chat';
    if (isOnChatPage && isWindowFocused && currentChannelId === channelId) {
      return;
    }

    // Prevent duplicate notifications for same channel within 2 seconds
    const now = Date.now();
    if (
      lastNotificationRef.current &&
      lastNotificationRef.current.channelId === channelId &&
      now - lastNotificationRef.current.timestamp < 2000
    ) {
      return;
    }

    lastNotificationRef.current = { channelId, timestamp: now };

    try {
      const notification = new Notification(title, {
        body,
        icon: '/logo.png',
        badge: '/logo.png',
        tag: `chat-${channelId}`, // Replace notification if same tag
        requireInteraction: false,
      });

      // Close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      // Play sound
      playNotificationSound();
    } catch (error) {
      console.error('Error showing notification:', error);
    }
  };

  // Listen for new messages
  useEffect(() => {
    if (!user) return;

    const unsubscribe = on('message:new', (message: any) => {
      // Don't notify for own messages
      if (message.userId === user.id) {
        return;
      }

      // Don't notify for thread replies (only notify for main channel messages)
      if (message.threadRootId) {
        return;
      }

      // Don't notify for system messages
      if (message.isSystem) {
        return;
      }

      // Get sender name (handle both camelCase and snake_case)
      let senderName = 'Someone';
      if (message.user) {
        const firstName = message.user.firstName || message.user.first_name || '';
        const lastName = message.user.lastName || message.user.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        senderName = fullName || message.user.email || 'Someone';
      }
      
      const messageText = message.text || '';
      const hasAttachment = !!message.attachmentUrl;
      
      // Create notification body
      let body = messageText;
      if (hasAttachment && message.attachmentName) {
        body = messageText ? `${messageText}\n📎 ${message.attachmentName}` : `📎 ${message.attachmentName}`;
      } else if (hasAttachment) {
        body = messageText || 'Sent an attachment';
      } else if (!messageText) {
        return; // Don't notify for empty messages
      }
      
      // Truncate long messages
      const truncatedBody = body.length > 100 
        ? body.substring(0, 100) + '...' 
        : body;

      showNotification(
        senderName,
        truncatedBody,
        message.channelId
      );
    });

    return unsubscribe;
  }, [user, on, location.pathname, isWindowFocused, currentChannelId]);

  // Request notification permission (call this from a user action)
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('Notification permission was denied');
      return false;
    }

    const permission = await Notification.requestPermission();
    setNotificationPermission(permission);
    return permission === 'granted';
  };

  return {
    notificationPermission,
    requestPermission,
    isWindowFocused,
  };
}
